//* Libraries imports
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

//* Hooks Imports
import { useNotification } from "@hooks/useNotification";

//* App imports
import { AppDispatch, RootState } from "@app/store";

//* Features imports
import { fetchPhaseFormById } from "../phaseForm/phaseFormSlice";
import { FormWizard } from "./FormWizard";
import {
  createFilledForm,
  setCurrentFilledFormId,
} from "../filledForm/filledFormSlice";
import { Status, FilledFormData } from "../filledForm/types";
import { filledFormService } from "../filledForm/filledFormService";

//* UI Components
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

//* Config imports
import { API_URL } from "@/config/api";

const FormSkeleton = () => (
  <div className="flex flex-col gap-4 animate-pulse">
    <div className="h-8 w-3/4 bg-background/20 rounded-md"></div>
    <div className="h-4 w-2/3 bg-background/20 rounded-md"></div>
    <div className="h-96 bg-background/10 rounded-xl mt-4"></div>
  </div>
);

const ErrorDisplay = ({
  error,
}: {
  error: { message: string; code: string; statusCode: number };
}) => (
  <div className="p-6 rounded-xl bg-background/10 text-center shadow-lg max-w-md mx-auto my-4">
    <h2 className="text-xl font-bold text-red-500 mb-4">
      Erro {error.statusCode}
    </h2>
    <p className="text-background mb-2">{error.message}</p>
    <p className="text-sm text-background/70">Código: {error.code}</p>
  </div>
);

const TokenInputModal = ({
  open,
  onClose,
  onSubmit,
  message,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (token: string) => void;
  message: string;
}) => {
  const [token, setToken] = useState<string>("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(token);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Token de Acesso</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            value={token}
            type="number"
            onChange={(e) => setToken(e.target.value)}
            placeholder="Digite o token recebido"
            maxLength={6}
            className="text-center text-background placeholder:text-background/60 border-0 border-b border-b-background rounded-none focus-visible:ring-0"
          />
          <DialogFooter>
            <Button type="submit" disabled={token.length !== 6}>
              Enviar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AlertDialog = ({
  open,
  onClose,
  title,
  content,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  content: string;
}) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{content}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type FormWizardPageProps = {
  clientId?: string | null;
};

export const FormWizardPage: React.FC<FormWizardPageProps> = ({
  clientId: clientIdProp,
}) => {
  console.log("🔄 Renderizando FormWizardPage");
  const navigate = useNavigate();
  const { id, processId, productId, clientId } = useParams<{
    id: string;
    processId: string;
    productId?: string;
    clientId?: string;
  }>();
  const resolvedClientId = clientIdProp ?? clientId;
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem: phaseForm, loading } = useSelector(
    (state: RootState) => {
      console.log("🔍 Selector executando, loading:", state.phaseForm.loading);
      return state.phaseForm;
    },
  );

  const [error, setError] = useState<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const [loading_data, setLoadingData] = useState<boolean>(false);
  const initialLoadDoneRef = useRef<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [forbiddenForm, setForbiddenForm] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>(
    "Token inválido. Tente novamente.",
  );
  const [isResendingToken, setIsResendingToken] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(true);
  const notification = useNotification();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [filledFormId, setFilledFormId] = useState<string | null>(null);
  const [items, setItems] = useState([]);

  // const { products: items, loading:loading_products, error:error_product } = useSelector((state: RootState) => state.product);
  // const state = useSelector((state: RootState) => state);
  // console.log(state)
  // Efeito para verificar o filledFormId no localStorage
  useEffect(() => {
    if (id && processId) {
      // Verificar primeiro na chave específica do formulário
      const formSpecificKey = `form_${id}_filledId`;
      const specificId = localStorage.getItem(formSpecificKey);

      if (specificId) {
        setFilledFormId(specificId);
        return;
      }

      // Verificar também na chave do storageKey
      const storageKey = `${id}_${processId}_v2`;
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        try {
          const data = JSON.parse(savedData);
          if (data.filledFormId) {
            setFilledFormId(data.filledFormId);
          }
        } catch (e) {
          console.error("❌ Erro ao analisar dados do storageKey:", e);
        }
      }
    }
  }, [id, processId]);

  const loadData = useCallback(async () => {
    if (!id || isLoadingRef.current || initialLoadDoneRef.current) {
      console.log("🚫 Ignorando carregamento:", {
        semId: !id,
        carregando: isLoadingRef.current,
        jaCarregado: initialLoadDoneRef.current,
      });
      return;
    }

    console.log("📡 Iniciando requisição fetchPhaseFormById");
    isLoadingRef.current = true;
    setLoadingData(true);

    try {
      const result = await dispatch(fetchPhaseFormById(id)).unwrap();
      let keys = await (await fetch(API_URL + "/products/keys")).json();
      setItems(keys);
      // await dispatch(fetchAllProductsKeys({}));

      console.log("✅ Requisição fetchPhaseFormById completada");
      initialLoadDoneRef.current = true;

      if (result.type === "CLOSED") {
        setForbiddenForm(true);
      }
    } catch (err) {
      console.error("❌ Erro ao carregar formulário:", err);
      setError("Erro ao carregar o formulário");
    } finally {
      isLoadingRef.current = false;
      setLoadingData(false);
    }
  }, [dispatch, id]);

  // Efeito único para carregamento inicial
  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (formData: Array<{ field: any; data: any }>) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const key = `${id}_${processId}_v2`;
    const localData = localStorage.getItem(key);
    const localDataJson = JSON.parse(localData || "{}");
    const newProcessId = localDataJson.processId;

    try {
      const payload: FilledFormData = {
        phaseForm: id,
        processId: newProcessId,
        data: formData,
        status: Status.COMPLETED,
        ...(resolvedClientId && { clientId: resolvedClientId }),
      };

      console.log("📝 Form submitted:", payload);

      const result = await dispatch(createFilledForm(payload)).unwrap();

      if (result && result.data._id) {
        console.log("📝 Filled form created with ID:", result.data._id);

        // Armazenar o ID no estado local
        setFilledFormId(result.data._id);

        // Armazenar o ID no Redux para acesso global
        dispatch(setCurrentFilledFormId(result.data._id));
      }

      notification.showSuccess("Formulário enviado com sucesso!");
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      notification.showError("Erro ao enviar o formulário. Tente novamente.");
      setError("Erro ao enviar o formulário");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTokenSubmit = async (token: string) => {
    try {
      const result = await filledFormService.validateToken(
        processId || "",
        token,
      );
      if ("data" in result && result.data?.valid) {
        setModalOpen(false);
        setShowForm(true);
        setForbiddenForm(false);
      } else {
        // O backend responde 200 com { valid:false, reason:'INVALID'|'EXPIRED' }.
        // Token expirado não adianta redigitar: o caminho é pedir um novo.
        const expired =
          "data" in result && result.data?.reason === "EXPIRED";
        setAlertMessage(
          expired
            ? "Seu token expirou. Use o botão \"Reenviar Token\" para receber um novo por WhatsApp."
            : "Token inválido. Tente novamente.",
        );
        setAlertOpen(true);
      }
    } catch (error) {
      console.error("❌ Error validating token:", error);
      setAlertMessage("Token inválido. Tente novamente.");
      setAlertOpen(true);
    }
  };

  const handleResendToken = async () => {
    if (!processId || isResendingToken) return;

    setIsResendingToken(true);
    try {
      const result = await filledFormService.resendToken(processId);

      if ("data" in result && result.data?.success !== false) {
        notification.showSuccess(
          result.data?.message ||
            "Token reenviado. Confira o WhatsApp do número cadastrado.",
        );
      } else {
        notification.showError(
          "Não foi possível reenviar o token. Tente novamente.",
        );
      }
    } catch (error) {
      console.error("❌ Error resending token:", error);
      notification.showError(
        "Não foi possível reenviar o token. Tente novamente.",
      );
    } finally {
      setIsResendingToken(false);
    }
  };

  if (loading || loading_data == true) {
    return (
      <div className="container mx-auto px-1 sm:px-4">
        <FormSkeleton />
      </div>
    );
  }

  if (error || !phaseForm || !processId) {
    return (
      <ErrorDisplay
        error={{
          message: error || "Formulário não encontrado",
          code: "FORM_ERROR",
          statusCode: 404,
        }}
      />
    );
  }

  function handleOnCloseModal(): void {
    setModalOpen(false);
    setForbiddenForm(true);
  }

  return (
    <div className="relative h-screen w-full overflow-hidden">
      <div className="container mx-auto h-full px-1 sm:px-4">
        {processId === "preview" && (
          <div className="bg-background/10 rounded-lg shadow p-2 sm:p-4 mb-4">
            <div className="flex items-center">
              <p className="text-sm text-background/70">
                Essa é uma visualização de preview. Clique no botão ao lado para
                voltar para o menu.
              </p>
              <div className="ml-auto">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  Voltar
                </Button>
              </div>
            </div>
          </div>
        )}

        {forbiddenForm && (
          <div className="bg-background/10 rounded-xl p-4 sm:p-6 shadow-lg">
            <h2 className="text-xl font-bold text-background mb-2">
              Formulário Seguro
            </h2>
            <p className="text-background/80 mb-6">
              Você só pode acessar o formulário se digitar o token correto que
              foi enviado por WhatsApp.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={() => setModalOpen(true)}>Digitar Token</Button>
              <Button
                variant="outline"
                onClick={handleResendToken}
                disabled={isResendingToken}
              >
                {isResendingToken ? "Reenviando..." : "Reenviar Token"}
              </Button>
            </div>
          </div>
        )}

        {showForm && !forbiddenForm && (
          <div className="h-full">
            <FormWizard
              id={id || ""}
              formFields={phaseForm.formFields || []}
              onSubmit={handleSubmit}
              processId={processId}
              storageKey={`${id}_${processId}_v2`}
              title={phaseForm.title || "Seja bem vindo!"}
              subtitle={phaseForm.subtitle || "Formulário"}
              description={phaseForm.short_description || ""}
              descriptionTitle={phaseForm.subtitle || "Formulário"}
              descriptionText={phaseForm.description || ""}
              phaseForm={phaseForm}
              filledFormId={filledFormId}
              isSubmitting={isSubmitting}
              products={items}
            />
          </div>
        )}

        <TokenInputModal
          open={modalOpen}
          onClose={() => handleOnCloseModal()}
          onSubmit={handleTokenSubmit}
          message="Por favor, insira o token recebido no seu WhatsApp."
        />

        <AlertDialog
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          title="Erro"
          content={alertMessage}
        />
      </div>
    </div>
  );
};
